import { useEffect, useState } from "react";
import { createBlockedTime, getStaff } from "../lib/api.ts";

interface StaffMember {
  id: string;
  name: string;
  is_active: number;
}

interface BlockTimeModalProps {
  slug: string;
  defaultStaffId?: string;
  defaultDate?: string;
  /** "HH:MM" — prefill the start time (end auto-fills to start + 1h). */
  defaultStartTime?: string;
  onClose: () => void;
  onCreated: () => void;
}

function localDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function addHourHHMM(hhmm: string, hours = 1): string {
  const [h, m] = hhmm.split(":").map(Number);
  const minutes = h * 60 + (m || 0) + hours * 60;
  const hh = String(Math.min(23, Math.floor(minutes / 60))).padStart(2, "0");
  const mm = String(minutes % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

export function BlockTimeModal({
  slug,
  defaultStaffId,
  defaultDate,
  defaultStartTime,
  onClose,
  onCreated,
}: BlockTimeModalProps) {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const today = localDateStr(new Date());
  const [staffId, setStaffId] = useState<string>(defaultStaffId ?? "");
  const [date, setDate] = useState<string>(defaultDate ?? today);
  const [startTime, setStartTime] = useState<string>(defaultStartTime ?? "12:00");
  const [endTime, setEndTime] = useState<string>(
    defaultStartTime ? addHourHHMM(defaultStartTime, 1) : "13:00",
  );
  const [reason, setReason] = useState<string>("");
  const [repeatWeekly, setRepeatWeekly] = useState(false);
  const [repeatUntil, setRepeatUntil] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await getStaff(slug);
        if (cancelled) return;
        const list: StaffMember[] = Array.isArray(resp)
          ? resp
          : Array.isArray((resp as { data?: StaffMember[] }).data)
            ? (resp as { data: StaffMember[] }).data
            : [];
        const active = list.filter((s) => s.is_active);
        setStaff(active);
        if (active.length > 0 && !staffId) setStaffId(active[0].id);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, staffId]);

  const applyPreset = (preset: "full" | "am" | "pm" | "hour") => {
    if (preset === "full") {
      setStartTime("00:00");
      setEndTime("23:59");
    } else if (preset === "am") {
      setStartTime("09:00");
      setEndTime("13:00");
    } else if (preset === "pm") {
      setStartTime("13:00");
      setEndTime("17:00");
    } else if (preset === "hour") {
      const now = new Date();
      const h = String(now.getHours()).padStart(2, "0");
      const m = String(now.getMinutes()).padStart(2, "0");
      setStartTime(`${h}:${m}`);
      const endH = String((now.getHours() + 1) % 24).padStart(2, "0");
      setEndTime(`${endH}:${m}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!staffId) return setError("Please pick a staff member");
    if (!date) return setError("Please pick a date");
    if (!startTime || !endTime) return setError("Start and End times are required");
    if (endTime <= startTime) return setError("End must be after Start");
    if (repeatWeekly && !repeatUntil) return setError("Choose a date to repeat until");
    if (repeatWeekly && repeatUntil <= date) {
      return setError("'Repeat until' must be after the start date");
    }

    setSubmitting(true);
    try {
      const startAt = new Date(`${date}T${startTime}:00`).toISOString();
      const endAt = new Date(`${date}T${endTime}:00`).toISOString();
      await createBlockedTime(slug, staffId, {
        start_at: startAt,
        end_at: endAt,
        reason: reason.trim() || undefined,
        ...(repeatWeekly ? { recurrence: "weekly", until: repeatUntil } : {}),
      });
      onCreated();
      onClose();
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center">
      <div
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.55)" }}
        onClick={submitting ? undefined : onClose}
      />
      <form
        onSubmit={handleSubmit}
        className="relative w-full md:max-w-md rounded-t-2xl md:rounded-2xl max-h-[92vh] overflow-y-auto"
        style={{
          background: "var(--card-bg)",
          border: "1px solid var(--card-border)",
        }}
      >
        <div className="flex justify-center pt-3 pb-2 md:hidden">
          <div
            className="w-10 h-1 rounded-full"
            style={{ background: "var(--border)" }}
          />
        </div>

        <div
          className="flex items-center justify-between px-5 py-3"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <h2 style={{ fontSize: "1.05rem", fontWeight: 600, color: "var(--text)" }}>
            Block time
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-lg"
            style={{ color: "var(--text-muted)" }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="px-5 py-4 flex flex-col gap-3">
          {loading ? (
            <div style={{ color: "var(--text-muted)", fontSize: "0.9rem", padding: "1rem 0" }}>
              Loading…
            </div>
          ) : (
            <>
              <div>
                <label className="label">Staff</label>
                <select
                  className="input"
                  value={staffId}
                  onChange={(e) => setStaffId(e.target.value)}
                >
                  {staff.length === 0 && <option value="">— no staff —</option>}
                  {staff.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Date</label>
                <input
                  type="date"
                  className="input"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>

              <div className="flex gap-2">
                <div style={{ flex: 1 }}>
                  <label className="label">Start</label>
                  <input
                    type="time"
                    className="input"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="label">End</label>
                  <input
                    type="time"
                    className="input"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </div>
              </div>

              {/* Quick presets */}
              <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => applyPreset("full")} className="btn-secondary text-xs py-1 px-2">
                  Full day
                </button>
                <button type="button" onClick={() => applyPreset("am")} className="btn-secondary text-xs py-1 px-2">
                  Morning
                </button>
                <button type="button" onClick={() => applyPreset("pm")} className="btn-secondary text-xs py-1 px-2">
                  Afternoon
                </button>
                <button type="button" onClick={() => applyPreset("hour")} className="btn-secondary text-xs py-1 px-2">
                  Next 1h
                </button>
              </div>

              <div>
                <label className="label">Reason</label>
                <input
                  className="input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Lunch, Vacation, Doctor"
                />
              </div>

              <div
                style={{
                  height: "1px",
                  background: "var(--border)",
                  margin: "0.25rem 0",
                }}
              />

              <label className="flex items-center gap-2 text-sm" style={{ color: "var(--text)" }}>
                <input
                  type="checkbox"
                  checked={repeatWeekly}
                  onChange={(e) => {
                    setRepeatWeekly(e.target.checked);
                    if (e.target.checked && !repeatUntil) {
                      // Default: 4 weeks out
                      const d = new Date(date);
                      d.setDate(d.getDate() + 28);
                      setRepeatUntil(localDateStr(d));
                    }
                  }}
                  className="h-4 w-4"
                />
                Repeat weekly
              </label>

              {repeatWeekly && (
                <div>
                  <label className="label">Until</label>
                  <input
                    type="date"
                    className="input"
                    value={repeatUntil}
                    onChange={(e) => setRepeatUntil(e.target.value)}
                    min={date}
                  />
                  <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                    Will block this time every week until the chosen date.
                  </div>
                </div>
              )}

              {error && (
                <div className="alert-error" style={{ marginTop: "0.25rem" }}>
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div
          className="flex gap-2 px-5 py-3"
          style={{ borderTop: "1px solid var(--border)" }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="btn-secondary"
            style={{ flex: 1 }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || loading}
            className="btn-primary"
            style={{ flex: 1 }}
          >
            {submitting ? "Blocking…" : "Block time"}
          </button>
        </div>
      </form>
    </div>
  );
}
